import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    useGetAllInvitesQuery,
    useGetAllStaffQuery,
    useGetStaffStatisticsQuery,
    useGetRolesQuery,
} from "../redux/services/staffApi";
import { Staff } from "../components/Table/StaffTable";

export const useStaffController = () => {
    const [searchParams] = useSearchParams();
    const viewParam = searchParams.get("view");
    const [view, setView] = useState<
        "overview" | "invitation" | "manualUpload" | "profile"
    >(viewParam === "invitation" ? "invitation" : "overview");
    // ?view=invitation (e.g. "Manage pending invites") opens the Invitations tab.
    useEffect(() => {
        if (viewParam === "invitation") setView("invitation");
    }, [viewParam, searchParams]);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // stats
    const { data: statsResponse, refetch } = useGetStaffStatisticsQuery();
    const userStats = statsResponse?.data?.user_stats || {};
    const invitationStats = statsResponse?.data?.invitation_stats || {};
    const totalStaff = userStats.total ?? 0;
    const activeStaff = userStats.active ?? 0;
    const inactiveStaff = userStats.inactive ?? 0;

    const totalInvites = invitationStats.total ?? 0;
    const pendingInvites = invitationStats.pending ?? 0;
    const rejectedInvites = invitationStats.rejected ?? 0;

    // roles
    const { data: rolesResponse } = useGetRolesQuery();
    const roleMap = useMemo(() => {
        const roles = rolesResponse?.data?.roleInfo?.data || [];
        const map: Record<string, string> = {};
        roles.forEach((r: any) => {
            if (r?.id && r?.name) map[r.id] = r.name;
        });
        return map;
    }, [rolesResponse]);

    // helpers 
    const formatDateTime = (dateString?: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";

        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
        )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
            date.getSeconds()
        )}`;
    };

    const normalizeStatus = (status?: string) => {
        switch (status?.toUpperCase()) {
            case "ACTIVE":
                return "Active";
            case "INACTIVE":
                return "In-active";
            case "PENDING":
                return "Pending";
            case "REJECTED":
            case "DECLINED":
                return "Declined";
            default:
                return "Active";
        }
    };

    //  pagination
    const [staffPage, setStaffPage] = useState(1);
    const [invitePage, setInvitePage] = useState(1);
    const pageSize = 15;


    // staff data
    const { data: staffResponse, isLoading: staffLoading } = useGetAllStaffQuery({
        page: staffPage,
        limit: pageSize,
    });

    const staffData: Staff[] = useMemo(() => {
        const staffList = staffResponse?.data?.data || [];
        if (!Array.isArray(staffList)) return [];

        return staffList.map((row: any) => ({
            id: row.id,
            firstName: row.name?.split(" ")[0] || "",
            lastName: row.name?.split(" ")[1] || "",
            name: row.name || "",
            email: row.email || "",
            role: row.role || roleMap[row.role_id] || "-",
            branch: row.branch?.branch_name || "-",
            branch_id: row.branch_id || row.branch?.id || "",
            status: normalizeStatus(row.status),
            dateCreated: formatDateTime(row.created_at),
            dateOfResumption: "",
            phone_number: row.phone_number || "",
            profileImage: row.profile_image || "",
            gender: row.gender,
            role_id: row.role_id,

            company_id: row.company_id,
            company_name: row.company?.name || "",


        }));
    }, [staffResponse, roleMap]);

    const staffLastPage =
        staffResponse?.data?.lastPage ||
        staffResponse?.data?.meta?.last_page ||
        1;

    const staffTotal = staffResponse?.data?.meta?.total;

    // invites data
    const { data: invitesResponse } = useGetAllInvitesQuery({
        page: invitePage,
        limit: pageSize,
    });
    // console.log('hello' ,  invitesResponse  )

    const invitationTableData: Staff[] = useMemo(() => {
        const invites = invitesResponse?.data?.data || [];
        if (!Array.isArray(invites)) return [];

        return invites.map((row: any) => ({
            id: row.id,
            firstName: row.first_name || "",
            lastName: row.last_name || "",
            name:
                `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
                "Unnamed Invite",
            email: row.email || "",
            role: row.role_name || roleMap[row.role_id] || "-",
            branch: row.branch_name || "-",
            branch_id: row.branch_id || "",
            status: normalizeStatus(row.status),
            dateCreated: formatDateTime(row.created_at),
            dateOfResumption: formatDateTime(row.resumption_date),
            phone_number: row.phone_number || "",
            company_name: row.company?.name || "",
        }));
    }, [invitesResponse, roleMap]);

    const inviteLastPage =
        invitesResponse?.data?.lastPage ||
        invitesResponse?.data?.meta?.last_page ||
        1;

    const inviteTotal = invitesResponse?.data?.meta?.total;

    return {
        view,
        setView,
        showBulkUploadModal,
        setShowBulkUploadModal,

        totalStaff,
        activeStaff,
        inactiveStaff,
        totalInvites,
        pendingInvites,
        rejectedInvites,

        staffData,
        refetch,
        staffLoading,
        staffPage,
        setStaffPage,
        staffLastPage,
        staffTotal,

        invitationTableData,
        invitePage,
        setInvitePage,
        inviteLastPage,
        inviteTotal,

        pageSize,
    };
};
